import type { RawPackageDates } from '../types';
import type { DateKind, DateVariableState, Template, ThemeVariable } from '../wysiwyg/types';
import { getUsedGeneratedDateVariables } from '../wysiwyg/themeVariables';

export type ResolvedPrintDate = {
  value: string;
  state: DateVariableState;
};

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

const DATE_SOURCE_ALIASES: Partial<Record<DateKind, string[]>> = {
  packagedDate: ['PackagedDate', 'packagedDate'],
  useByDate: ['UseByDate', 'useByDate'],
  sellByDate: ['SellByDate', 'sellByDate'],
  shelfLifeDate: ['Shelflifedate', 'ShelfLifeDate', 'shelflifedate', 'shelfLifeDate'],
  expirationDate: ['ExpirationDate', 'expirationDate'],
  bestByDate: [
    'UseByDate', 'useByDate', 'SellByDate', 'sellByDate', 'ExpirationDate', 'expirationDate',
    'Shelflifedate', 'ShelfLifeDate', 'shelflifedate', 'shelfLifeDate',
  ],
  harvestDate: ['HarvestDate', 'harvestDate', 'harvestDate-v2'],
  cultivationDate: ['CultivationDate', 'cultivationDate'],
};

export const normalizeDateOnly = (value: unknown): string => {
  if (typeof value !== 'string' && !(value instanceof Date)) return '';
  const text = value instanceof Date ? value.toISOString() : value.trim();
  const match = text.match(DATE_ONLY) || text.match(/^(\d{4})-(\d{2})-(\d{2})T/);
  if (!match) return '';
  const result = `${match[1]}-${match[2]}-${match[3]}`;
  const date = new Date(`${result}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === result ? result : '';
};

export const mergePackageDateSources = (
  coa: Record<string, any>,
  packageData: Record<string, any>,
) => {
  const merged = { ...packageData, ...coa };
  const firstValidDate = (source: Record<string, any>, keys: string[]) => {
    for (const key of keys) {
      const value = normalizeDateOnly(key === 'rawPackageDates.expirationDate'
        ? source.rawPackageDates?.expirationDate
        : source[key]);
      if (value) return value;
    }
    return '';
  };
  const firstNonBlankDate = (source: Record<string, any>, keys: string[]) => {
    for (const key of keys) {
      const value = key === 'rawPackageDates.expirationDate'
        ? source.rawPackageDates?.expirationDate
        : source[key];
      if (value != null && (typeof value !== 'string' || value.trim() !== '')) return value;
    }
    return '';
  };
  const firstValidRawDate = (source: Record<string, any>, key: string) => {
    const entry = Object.entries(source).find(([sourceKey]) => sourceKey.toLowerCase() === key.toLowerCase());
    return normalizeDateOnly(entry?.[1]);
  };

  const coaRawDates = coa.rawPackageDates;
  const packageRawDates = packageData.rawPackageDates;
  if (
    (!coaRawDates || typeof coaRawDates !== 'object' || Array.isArray(coaRawDates))
    && packageRawDates && typeof packageRawDates === 'object' && !Array.isArray(packageRawDates)
  ) {
    const rawDates = { ...packageRawDates };
    const rawDateOverrides = [
      { key: 'packagedDate', aliases: ['packagedDate', 'PackagedDate'] },
      { key: 'useByDate', aliases: ['useByDate', 'UseByDate'] },
      { key: 'sellByDate', aliases: ['sellByDate', 'SellByDate'] },
      { key: 'expirationDate', aliases: ['expirationDate', 'ExpirationDate'] },
    ];
    for (const { key, aliases } of rawDateOverrides) {
      const coaDate = firstValidDate(coa, aliases);
      if (!coaDate) continue;
      const rawUseByDate = firstValidRawDate(rawDates, 'useByDate');
      if (
        key === 'expirationDate'
        && rawUseByDate
        && coaDate === rawUseByDate
      ) continue;
      Object.keys(rawDates).forEach((rawKey) => {
        if (rawKey.toLowerCase() === key.toLowerCase()) delete rawDates[rawKey];
      });
      rawDates[key] = coaDate;
    }
    merged.rawPackageDates = rawDates;
  }

  merged.expirationDate = firstValidDate(coa, [
    'rawPackageDates.expirationDate', 'expirationDate', 'ExpirationDate',
  ]) || firstValidDate(packageData, [
    'rawPackageDates.expirationDate', 'expirationDate', 'ExpirationDate',
  ]) || firstNonBlankDate(coa, [
    'rawPackageDates.expirationDate', 'expirationDate', 'ExpirationDate',
  ]) || firstNonBlankDate(packageData, [
    'rawPackageDates.expirationDate', 'expirationDate', 'ExpirationDate',
  ]);
  merged.cultivationDate = firstValidDate(coa, ['cultivationDate', 'CultivationDate'])
    || firstValidDate(packageData, ['cultivationDate', 'CultivationDate'])
    || firstNonBlankDate(coa, ['cultivationDate', 'CultivationDate'])
    || firstNonBlankDate(packageData, ['cultivationDate', 'CultivationDate']);
  return withCanonicalDateAliases(merged);
};

export const addCalendarDays = (dateOnly: string, days: number | null | undefined): string => {
  const normalized = normalizeDateOnly(dateOnly);
  if (!normalized || !Number.isSafeInteger(days) || Number(days) < 0) return '';
  const date = new Date(`${normalized}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + Number(days));
  if (!Number.isFinite(date.getTime()) || date.getUTCFullYear() > 9999) return '';
  return date.toISOString().slice(0, 10);
};

export const getDateSourceSignature = (source: Record<string, unknown>): string => {
  const entries = Object.entries(source || {});
  const aliases = [...new Set(Object.values(DATE_SOURCE_ALIASES).flat())];
  const flatSignature = aliases.map((alias) => {
    const entry = entries.find(([key]) => key.toLowerCase() === alias.toLowerCase());
    return `${alias}:${normalizeDateOnly(entry?.[1])}`;
  }).join('|');
  const rawDates = source.rawPackageDates;
  const rawSignature = rawDates && typeof rawDates === 'object'
    ? Object.entries(rawDates).map(([key, value]) => `${key}:${normalizeDateOnly(value)}`).sort().join('|')
    : '';
  return `${flatSignature}|rawPackageDates:${rawSignature}`;
};

const sourceValue = (source: Record<string, unknown>, aliases: string[]) => {
  const entries = Object.entries(source || {});
  for (const alias of aliases) {
    const entry = entries.find(([key]) => key.toLowerCase() === alias.toLowerCase());
    const normalized = normalizeDateOnly(entry?.[1]);
    if (normalized && entry) return { value: normalized, source: entry[0] };
  }
  return null;
};

const RAW_DATE_ALIASES: Partial<Record<DateKind, Array<keyof RawPackageDates>>> = {
  packagedDate: ['packagedDate'],
  useByDate: ['useByDate'],
  sellByDate: ['sellByDate'],
  expirationDate: ['expirationDate'],
  bestByDate: ['useByDate', 'sellByDate', 'expirationDate'],
};

const firstNonBlankDateSource = (source: Record<string, unknown>, aliases: string[]) => {
  const entries = Object.entries(source || {});
  for (const alias of aliases) {
    const entry = entries.find(([key]) => key.toLowerCase() === alias.toLowerCase());
    const value = entry?.[1];
    if (value != null && (typeof value !== 'string' || value.trim() !== '')) return value;
  }
  return undefined;
};

const resolveDateSource = (kind: DateKind, source: Record<string, unknown>) => {
  const rawDates = source.rawPackageDates;
  const rawAliases = RAW_DATE_ALIASES[kind];
  if (rawAliases && rawDates && typeof rawDates === 'object' && !Array.isArray(rawDates)) {
    const sourced = sourceValue(rawDates as Record<string, unknown>, rawAliases);
    if (sourced) return { ...sourced, source: `rawPackageDates.${sourced.source}` };

    // A supplied raw contract is authoritative for package dates. Shelf-life and harvest
    // aliases remain eligible because they are not represented by rawPackageDates.
    if (kind === 'bestByDate') {
      return sourceValue(source, DATE_SOURCE_ALIASES.shelfLifeDate || []);
    }
    return null;
  }
  return sourceValue(source, DATE_SOURCE_ALIASES[kind] || []);
};

export const withCanonicalDateAliases = (coa: Record<string, any>): Record<string, any> => {
  if (!coa || typeof coa !== 'object') return coa;

  const out = { ...coa };
  for (const kind of Object.keys(DATE_SOURCE_ALIASES) as DateKind[]) {
    const resolved = resolveDateSource(kind, coa);
    if (resolved) {
      out[kind] = resolved.value;
      continue;
    }

    const rawDates = coa.rawPackageDates;
    const rawAliases = RAW_DATE_ALIASES[kind];
    const rawFallback = rawAliases && rawDates && typeof rawDates === 'object' && !Array.isArray(rawDates)
      ? firstNonBlankDateSource(rawDates as Record<string, unknown>, rawAliases)
      : undefined;
    const hasRawDateContract = Boolean(
      rawAliases && rawDates && typeof rawDates === 'object' && !Array.isArray(rawDates),
    );
    const flatAliases = kind === 'bestByDate'
      ? [...(DATE_SOURCE_ALIASES[kind] || []), 'BestByDate']
      : DATE_SOURCE_ALIASES[kind] || [];
    const fallback = rawFallback
      ?? (hasRawDateContract
        ? kind === 'bestByDate'
          ? firstNonBlankDateSource(coa, DATE_SOURCE_ALIASES.shelfLifeDate || [])
          : undefined
        : firstNonBlankDateSource(coa, flatAliases));
    if (fallback !== undefined) out[kind] = fallback;
  }
  return out;
};

export const resolveAutomaticPrintDate = (
  kind: DateKind,
  source: Record<string, unknown>,
  packagedDate: string,
  shelfLifeDays?: number | null,
): ResolvedPrintDate => {
  const sourced = resolveDateSource(kind, source);
  if (sourced) return { value: sourced.value, state: { provenance: 'source', source: sourced.source } };
  if (kind === 'packagedDate' && packagedDate) {
    return { value: normalizeDateOnly(packagedDate), state: { provenance: 'source', source: 'confirmedPackagedDate' } };
  }
  if (kind === 'expirationDate' || kind === 'bestByDate') {
    const calculated = addCalendarDays(packagedDate, shelfLifeDays);
    if (calculated) return { value: calculated, state: { provenance: 'calculated', source: 'shelfLifeDays' } };
  }
  return { value: '', state: { provenance: 'source' } };
};

export const getEffectiveShelfLifeDays = (
  productDays: unknown,
  templateDays: unknown,
): number | undefined => {
  for (const value of [productDays, templateDays]) {
    if (Number.isSafeInteger(value) && Number(value) >= 0) return Number(value);
  }
  return undefined;
};

const isBlankDate = (value: unknown) => value == null || (typeof value === 'string' && value.trim() === '');

export const withCalculatedPrintDates = (
  coa: Record<string, any>,
  packagedDate: string,
  shelfLifeDays?: number | null,
  controlledDateVariables: readonly ThemeVariable[] = [],
): Record<string, any> => {
  const calculated = addCalendarDays(packagedDate, shelfLifeDays);
  if (!calculated || !coa) return coa;
  const controlledKinds = new Set(controlledDateVariables.map(({ dateKind }) => dateKind));
  const sourceExpirationDate = coa.expirationDate;
  const expirationDate = controlledKinds.has('expirationDate')
    ? sourceExpirationDate
    : isBlankDate(sourceExpirationDate) ? calculated : sourceExpirationDate;
  const bestByFallback = isBlankDate(sourceExpirationDate) ? calculated : sourceExpirationDate;
  return {
    ...coa,
    expirationDate,
    bestByDate: controlledKinds.has('bestByDate') || !isBlankDate(coa.bestByDate)
      ? coa.bestByDate
      : bestByFallback,
  };
};

export const getUsedPrintDateVariables = (template: Template | null): ThemeVariable[] =>
  template ? getUsedGeneratedDateVariables(template.elements, template.themeVariables) : [];
